import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Spinner, Typography } from '@glplus/glpweb-dsl';
import { Panel } from './Panel';
import { runnerApi, RunnerApiError, type MyVerificationCall } from '../lib/runnerApiClient';
import { ORANGE } from '../styles/colors';

const LINK_BUTTON_SX = { display: 'inline-block', px: 2, py: 1, borderRadius: 2, backgroundColor: ORANGE, color: '#fff', fontWeight: 700, fontSize: '14px', textDecoration: 'none' } as const;

/**
 * The 15-minute video call (Rod, 2026-10-01): staff match the Runner to their
 * photo ID and look at the insurance card. Booking happens on Calendly in a new
 * tab and reaches us by webhook, so the panel refetches when the tab regains focus.
 */
export function VerificationCallPanel({ onCompleted }: { onCompleted?: () => void }) {
  const { t, i18n } = useTranslation();
  const [state, setState] = useState<MyVerificationCall | null>(null);
  const [error, setError] = useState<string | null>(null);
  const completedRef = useRef(onCompleted);
  completedRef.current = onCompleted;

  const load = useCallback(async () => {
    try {
      const next = await runnerApi.myVerificationCall();
      setState(next);
      setError(null);
      if (next.call?.completedAt && next.applicationStatus !== 'verification_call') completedRef.current?.();
    } catch (e) {
      setError(e instanceof RunnerApiError ? e.message : t('call.loadFailed'));
    }
  }, [t]);

  useEffect(() => {
    void load();
    const refresh = () => {
      if (document.visibilityState === 'visible') void load();
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [load]);

  if (!state && !error) {
    return (
      <Panel title={t('call.title')} accent={ORANGE} dataTestId="verification-call">
        <Spinner aria-label={t('call.title')} />
      </Panel>
    );
  }

  const call = state?.call ?? null;
  const locale = i18n.language.startsWith('es') ? 'es-US' : 'en-US';

  if (call?.completedAt) {
    return (
      <Panel title={t('call.doneTitle')} accent="#2E7D32" dataTestId="verification-call">
        <Typography data-testid="verification-call-done" sx={{ color: '#4b5563', fontSize: '14px' }}>{t('call.doneBody')}</Typography>
      </Panel>
    );
  }

  const booked = call?.scheduledStart ? call : null;

  return (
    <Panel title={booked ? t('call.bookedTitle') : t('call.title')} accent={ORANGE} dataTestId="verification-call">
      {error && (
        <Box sx={{ mb: 1.5 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}
      <Typography sx={{ color: '#4b5563', fontSize: '14px' }}>{t('call.intro')}</Typography>
      <Typography sx={{ color: '#1f2937', fontSize: '14px', fontWeight: 700, mt: 1 }}>{t('call.ready')}</Typography>
      {booked ? (
        <Box sx={{ mt: 2 }}>
          <Typography data-testid="verification-call-time" sx={{ color: '#1f2937', fontSize: '15px', fontWeight: 800 }}>
            {new Date(booked.scheduledStart as string).toLocaleString(locale, { weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
          </Typography>
          {booked.interviewerName && <Typography sx={{ color: '#5f6b7a', fontSize: '13px' }}>{t('call.with', { name: booked.interviewerName })}</Typography>}
          {booked.joinUrl && (
            <Box sx={{ mt: 1.5 }}>
              <Box component="a" href={booked.joinUrl} target="_blank" rel="noopener noreferrer" aria-label={t('call.joinAria')} data-testid="verification-call-join" sx={LINK_BUTTON_SX}>
                {t('call.join')}
              </Box>
            </Box>
          )}
          <Typography sx={{ color: '#5f6b7a', fontSize: '12px', mt: 1.5 }}>{t('call.reschedule')}</Typography>
        </Box>
      ) : state?.bookingUrl ? (
        <Box sx={{ mt: 2 }}>
          <Box component="a" href={state.bookingUrl} target="_blank" rel="noopener noreferrer" aria-label={t('call.bookAria')} data-testid="verification-call-book" sx={LINK_BUTTON_SX}>
            {t('call.book')}
          </Box>
          <Typography sx={{ color: '#5f6b7a', fontSize: '12px', mt: 1.5 }}>{t('call.bookHint')}</Typography>
        </Box>
      ) : (
        <Typography data-testid="verification-call-unavailable" sx={{ color: '#5f6b7a', fontSize: '13px', mt: 2 }}>{t('call.notBookable')}</Typography>
      )}
    </Panel>
  );
}
